import { useEffect, useRef } from "react";
import type { RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared modal accessibility: Escape closes, focus is trapped inside the
 * panel and restored on close, and body scroll is locked while open.
 *
 * Attach the returned ref to the modal panel element and pass the same
 * close handler the panel's close button / backdrop uses:
 *
 *     const panelRef = useModalDismiss(onClose);
 *     <div className="modal-panel" ref={panelRef}> … </div>
 *
 * The close handler is read through a ref, so callers can pass an inline
 * arrow without re-running the effect.
 *
 * ``enabled`` gates the behaviour for modals that stay mounted and toggle
 * via an isOpen flag (pass isOpen). Conditionally-rendered modals can omit
 * it (defaults true).
 *
 * ``trapHeading`` is opt-in. The announcement drawer focuses a heading that
 * is not in the tabbable list. Other modals keep the original trap: Shift+Tab
 * wraps only from the first control or the panel itself.
 */
export function useModalDismiss<T extends HTMLElement = HTMLDivElement>(
  onClose: () => void,
  enabled = true,
  options?: { trapHeading?: boolean },
): RefObject<T | null> {
  const ref = useRef<T>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!enabled) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const trapHeading = options?.trapHeading === true;
    const panel = ref.current;
    // Move focus into the modal (first focusable child, else the panel).
    const initial = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (initial ?? panel)?.focus?.();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      const items = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (trapHeading) {
        const activeEl = active instanceof HTMLElement ? active : null;
        const inside = !!activeEl && panel.contains(activeEl);
        // The drawer heading is tabindex -1, so it is focused on open but not
        // in the tabbable list. Shift+Tab from it used to leave the dialog.
        if (!inside || activeEl === panel || !items.includes(activeEl)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }
        if (e.shiftKey && activeEl === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && activeEl === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [enabled, options?.trapHeading]);

  return ref;
}
